define('presentation/userProfile/viewModels/userProfileViewModel',['businessServices/converters/textConverter',
        'businessServices/events/eventManager',
        'businessServices/router/router',
        'common/collections/enumerationKeyValueConverter',
        'common/promises/promiseFactory',
        'constants/colorConstants',
        'constants/userPresenceStatus',
        'i18next',
        'presentation/common/window/windowControl',
        'presentation/userProfile/facades/userProfileFacade',
        'settings/navigationConfiguration',
        'settings/uiAnimationsConfiguration'
], function (
    /** @type typeof import('businessServices/converters/textConverter') */
    TextConverter,
    /** @type import('businessServices/events/eventManager') */
    eventManager,
    /** @type typeof import('businessServices/router/router') */
    router,
    /** @type import('common/collections/enumerationKeyValueConverter') */
    _keyValueConverter,
    /** @type typeof import('common/promises/promiseFactory') */
    PromiseFactory,
    /** @type typeof import('constants/colorConstants') */
    ColorConstants,
    /** @type typeof import('constants/userPresenceStatus') */
    UserPresenceStatus,
    /** @type typeof import('i18next') */
    i18n,
    /** @type import('presentation/common/window/windowControl') */
    windowControl,
    /** @type typeof import('presentation/userProfile/facades/userProfileFacade') */
    UserProfileFacade,
    /** @type typeof import('settings/navigationConfiguration') */
    navigationConfiguration,
    /** @type import('settings/uiAnimationsConfiguration') */
    UIAnimationsConfig
) {
    return function () {
        const self = this;

        const _promiseFactory = new PromiseFactory();
        const _textConverter = new TextConverter();

        /** @type { UserProfileFacade['prototype'] } */
        let _facade = null;
        /** @type { number } */
        let _hideUserAvatarMenuTimeout = null;

        const _getPresenceLabel = (/** @type { number } */ enumValue) => {
            switch (enumValue) {
                case UserPresenceStatus.Available:
                    return i18n.t('userProfile:available');
                case UserPresenceStatus.AtLunch:
                    return i18n.t('userProfile:atLunch');
                case UserPresenceStatus.Away:
                    return i18n.t('userProfile:away');
                case UserPresenceStatus.InAMeeting:
                    return i18n.t('userProfile:inAMeeting');
                case UserPresenceStatus.DoNotDisturb:
                    return i18n.t('userProfile:doNotDisturb');
                case UserPresenceStatus.GoneHome:
                    return i18n.t('userProfile:goneHome');
                case UserPresenceStatus.OutOfOffice:
                    return i18n.t('userProfile:outOfOffice');
                default:
                    return i18n.t('userProfile:available');
            }
        };

        const _getPresenceIndicatorColor = (/** @type { number } */ enumValue) => {
            switch (enumValue) {
                case UserPresenceStatus.Available:
                    return ColorConstants.colorTertiaryGreen;
                case UserPresenceStatus.AtLunch:
                case UserPresenceStatus.Away:
                case UserPresenceStatus.InAMeeting:
                    return ColorConstants.colorTertiaryOrange;
                case UserPresenceStatus.DoNotDisturb:
                case UserPresenceStatus.GoneHome:
                case UserPresenceStatus.OutOfOffice:
                    return ColorConstants.colorError300;
                default:
                    return ColorConstants.colorTertiaryGreen;
            }
        };

        const _onUserPresenceUpdated = (/** @type { IUserPresenceUpdatedEvent } */ event) => {
            return _promiseFactory.defer((promise) => {
                _facade.getCurrentUserPresence()
                    .fail(promise.reject)
                    .done((userPresence) => {
                        if (userPresence && userPresence.status) {
                            self.userPresence(userPresence.status);
                        }
                        promise.resolve();
                    });
            });
        };

        const _updateUserProfile = (/** @type { string } */ userId) => {
            const userProfileId = _facade.getCurrentUserProfileId();
            if (userId === userProfileId) {
                _refreshAvatar();
                _updateUserFullName();
            }
        };

        const _updateUserFullName = () => {
            return _promiseFactory.defer((promise) => {
                _facade.getCurrentUserInfo()
                    .fail(promise.reject)
                    .done((userProfile) => {
                        self.userName(userProfile.fullName);
                        promise.resolve();
                    });
            });
        };

        const _refreshAvatar = () => {
            return _promiseFactory.defer((promise) => {
                _facade.getCurrentUserAvatar()
                    .fail(promise.reject)
                    .done((avatar) => {
                        self.userAvatar(avatar);
                        self.showInitials(avatar.showDefaultImage());
                        promise.resolve();
                    });
            });
        };

        self.isBillingAdmin = ko.observable(false);
        self.isSystemAdmin = ko.observable(false);
        self.isImpersonating = ko.observable(false);
        self.isUserAvatarMenuOpen = ko.observable(false);
        self.isUserPresenceMenuOpen = ko.observable(false);
        self.userAvatar = ko.observable();
        self.userInitials = ko.observable("");
        self.userName = ko.observable("");
        self.showInitials = ko.observable(false);
        self.userPresence = ko.observable(UserPresenceStatus.Available);
        self.currentPresenceIndicatorColor = ko.pureComputed(() => _getPresenceIndicatorColor(self.userPresence()));
        self.currentPresenceI18nKey = ko.pureComputed(() => `userProfile:${_getPresenceLabel(self.userPresence())}`);

        self.userAvatarMenuOptions = ko.computed(() => {
            const options = [
                {textI18n: 'userSettings', iconName: 'userSettings', onClick: () => self.navigateToUserSettings(), showSeparator: true},
                {textI18n: 'helpCenter', iconName: 'userSettingsHelpCenter', onClick: () => self.openHelpCenter(), showSeparator: false},
                {textI18n: 'signOut', iconName: 'userSettingsSignOut', onClick: () => self.logUserOut(), showSeparator: true},
            ];

            if (self.isBillingAdmin()) {
                const billingSettings = {
                    textI18n: 'billing',
                    iconName: 'userSettingsBilling',
                    onClick: () => self.navigateToBillingSettings(),
                    showSeparator: true
                };
                options.splice(options.length - 1, 0, billingSettings);
            }

            if (self.isSystemAdmin()) {
                const systemSettings = {
                    textI18n: 'systemSettings',
                    iconName: 'userSettingsSystem',
                    onClick: () => self.navigateToSystemSettings(),
                    showSeparator: !self.isBillingAdmin()
                };
                options.splice(options.length - 1, 0, systemSettings);
            }

            return options;
        });

        self.userPresenceMenuOptions = _keyValueConverter
            .convertToKeyValues(UserPresenceStatus)
            .filter((option) => option.value !== UserPresenceStatus.Unknown)
            .map((option) => {
                const i18nKey = _textConverter.camelCase(option.key);
                return {
                    text: i18n.t(`userProfile:${i18nKey}`),
                    indicatorColor: _getPresenceIndicatorColor(option.value),
                    onClick: () => self.updateUserPresence(option.value)
                };
            });

        self.hideUserAvatarMenu = () => {
            _hideUserAvatarMenuTimeout = setTimeout(() => {
                self.isUserAvatarMenuOpen(false);
                self.isUserPresenceMenuOpen(false);
            }, UIAnimationsConfig.presentation_userProfile_viewModels_userProfileViewModel_hideUserAvatarMenu);
        };

        self.logUserOut = () => {
            _facade.logUserOut();
        };

        self.navigateToBillingSettings = () => {
            self.isUserAvatarMenuOpen(false);
            router.navigate(navigationConfiguration.billingProfile);
        };

        self.navigateToUserSettings = () => {
            self.isUserAvatarMenuOpen(false);
            router.navigate(navigationConfiguration.routesById.userProfile.url);
        };

        self.navigateToSystemSettings = () => {
            self.isUserAvatarMenuOpen(false);
            router.navigate(navigationConfiguration.routesById.settings.url);
        };

        self.openHelpCenter = () => {
            windowControl.openContactUs();
        };

        self.resetHideUserProfileOptions = () => {
            clearTimeout(_hideUserAvatarMenuTimeout);
        };

        self.updateUserPresence = (/** @type { number } */ userPresence) => {
            return _promiseFactory.defer((promise) => {
                self.toggleUserAvatarMenu();
                self.toggleUserPresenceMenu(false);
                _facade.updateCurrentUserPresence(userPresence)
                    .fail(promise.reject)
                    .done((result) => {
                        self.userPresence(result);
                        promise.resolve();
                    });
            });
        };

        self.toggleUserAvatarMenu = () => {
            self.isUserAvatarMenuOpen(!self.isUserAvatarMenuOpen());
        };

        self.toggleUserPresenceMenu = (/** @type { boolean } */ isVisible) => {
            self.isUserPresenceMenuOpen(isVisible);
        };

        self.activate = () => {
            _facade = new UserProfileFacade();
            _facade.init(_promiseFactory);

            return _initialize();
        };

        const _initialize = () => {
            _facade.getCurrentUserInfo()
                .done((userProfile) => {
                    self.userName(userProfile.fullName);
                    self.userInitials(userProfile.initials);
                });

            _facade.getCurrentUserPresence()
                .done((userPresence) => {
                    if (userPresence && userPresence.status) {
                        self.userPresence(userPresence.status);
                    }
                });

            _facade.isImpersonating().done((isImpersonating) => self.isImpersonating(isImpersonating));
            _facade.isBillingAdmin().done((isBillingAdmin) => self.isBillingAdmin(isBillingAdmin));
            _facade.isSystemAdmin().done((isSystemAdmin) => self.isSystemAdmin(isSystemAdmin));

            _refreshAvatar();

            _facade.onUserPresenceUpdated(_onUserPresenceUpdated);
            eventManager.subscribeUserInfoChanged(_updateUserProfile);

            return _promiseFactory.wait();
        };
    };
});

